import { useEffect, useMemo, useRef } from "react";
import { debounce } from "lodash";
import { useCoverageFundsQuery, useCoverageViewQuery } from "#graphql/supergraphHooks";
import {
  type CoverageCellStateWire,
  type CoverageQueryRow,
  toCoverageFilterStates,
} from "../utils/coverageGrid.utils";
import { isFayAuthFailure } from "../utils/fayAuthErrors";
import { useReportFayAuthFailure } from "./useFayAuthGate";
import type { ApolloError } from "@apollo/client";

// Checks run 1–2 min, so cell states change on a minute scale; 15s keeps the
// grid live without firing dozens of heavy fund×period queries per change.
const COVERAGE_POLL_MS = 15000;
// Collapse bursts of on-demand refreshes (e.g. approve fires the same tick as a
// poll) into one, so multiple heavy fund×period queries never race. Leading
// edge so the first call fires immediately; trailing off so a tail-in follower
// isn't scheduled after the burst.
const REFRESH_DEBOUNCE_MS = 1000;

type FilterOption = { value: string; label: string };

/** ISO-date `start`/`end` bounds, as produced by `getDefaultCoverageRange`. */
export type CoverageDateRange = { start: string; end: string };

export type CoverageFilters = {
  dateRange: CoverageDateRange;
  funds: string[];
  statuses: CoverageCellStateWire[];
};

export type CoverageData = {
  /** Every fund in the catalog, for the "Fund" dropdown. */
  fundOptions: FilterOption[];
  /** Filtered matrix rows for the grid. */
  rows: CoverageQueryRow[];
  /** Period column headers for the grid. */
  periods: string[];
  loading: boolean;
  /** The view-query error, or the seed-query error when it's a fay auth failure
   *  and the view query never ran (zero-packaged-funds arena, so it's `skip`ped)
   *  — otherwise `CoverageView` would have no error to key its gated-spinner /
   *  inline-error branches off of and would silently render an empty grid. */
  error?: ApolloError;
  /** On-demand refresh of the fund universe + fund catalog + filtered grid.
   *  Callers fire this after mutations that create/change deliverable packages
   *  (approve classification, close of a package slideout after triage) so the
   *  grid reflects immediately instead of waiting for the next 15s poll tick.
   *
   *  Internally: `stopPolling` on both polled queries → `Promise.all` of the
   *  three refetches (seed universe + fund catalog + filtered view) →
   *  `startPolling` to reset the 15s cadence. Leading-edge debounced (1s) so a
   *  refresh that lands on a poll tick collapses into one round-trip.
   *
   *  No-ops (and does NOT restart polling) when the session is auth-dead or the
   *  grid isn't ready (arena unresolved / seed still empty). */
  refreshCoverage: () => void;
};

/**
 * Owns all coverage-matrix data orchestration so `CoverageView` stays a thin
 * presentational shell. Runs three queries and derives the dropdown options:
 *
 * 1. seed-fund universe (fay's `coverageFunds`) — only the funds with at least
 *    one deliverable package in the selected range, so the grid renders packaged
 *    funds only. Date-scoped to match `coverageView`; fed into it via
 *    `seedFundIds`.
 * 2. unfiltered catalog — populates the dropdown, kept stable across filter
 *    changes.
 * 3. filtered view — the matrix the grid renders, filtered server-side.
 *
 * @param arenaId The coverage view is arena-scoped server-side (fay requires
 * arenaId); undefined until the host app resolves its arena, which skips the
 * view queries so the required arg is never issued as NaN.
 */
export function useCoverageData(
  { dateRange, funds, statuses }: CoverageFilters,
  arenaId: number | undefined,
): CoverageData {
  // Seed-fund universe — only the funds with at least one deliverable package in
  // the selected range (fay's `coverageFunds`), so the grid renders packaged
  // funds only. Date-scoped to the same range as `coverageView` so the universe
  // stays in lockstep with the grid data; skipped until a valid arena resolves
  // since the seed is arena-scoped server-side. The ids thread into Fay's
  // coverageView via `seedFundIds`.
  const {
    data: seedData,
    loading: seedLoading,
    error: seedError,
    stopPolling: stopSeedPolling,
    startPolling: startSeedPolling,
    refetch: refetchSeed,
  } = useCoverageFundsQuery({
    variables: {
      input: {
        arenaId,
        dateRange: { start: dateRange.start, end: dateRange.end },
      },
    },
    fetchPolicy: "cache-and-network",
    skip: arenaId === undefined,
    // Poll the universe too, not just the cell states: a package assigned to a
    // fund not yet in the grid must add its row live. When the seed picks up the
    // new fund, seedFundIds grows and the coverageView queries re-fire on the
    // changed variables — no manual refresh needed.
    pollInterval: COVERAGE_POLL_MS,
    skipPollAttempt: () => document.hidden,
  });
  const seedFundIds = useMemo(
    () => seedData?.coverageFunds?.map((f) => f.fund.id) ?? [],
    [seedData],
  );

  // Catalog fetch — returns every fund in the seed universe with no filter
  // applied, so the Fund dropdown always lists every fund and stays stable
  // across filter changes. Filtering still happens server-side via the second
  // query below; this catalog only exists to populate the dropdown.
  // TODO(scale): when the tenant fund count grows beyond a few hundred, replace
  // this with a dedicated paginated/searchable `funds` query with debounced
  // server-side search.
  const { data: catalogData, refetch: refetchCatalog } = useCoverageViewQuery({
    variables: {
      input: {
        arenaId: arenaId!,
        groupBy: "FUND_BY_PERIOD",
        dateRange: { start: dateRange.start, end: dateRange.end },
        seedFundIds,
        filter: {},
      },
    },
    fetchPolicy: "cache-and-network",
    // Skip until we have a seed universe; sending `[]` would deterministically
    // return zero rows and waste a round trip + clobber the cached catalog.
    skip: seedFundIds.length === 0 || arenaId === undefined,
  });

  const catalogRows = useMemo(() => catalogData?.coverageView.rows ?? [], [catalogData]);

  // Flat dropdown: one option per catalog fund, no family grouping. Every fund
  // a package can be bound to is selectable here, matching the flat grid.
  const fundOptions = useMemo(
    () =>
      catalogRows.map((row) => ({ value: row.fund.id, label: row.fund.name || row.fund.id })),
    [catalogRows],
  );

  // The Fund picker drives the query directly — each pick filters to exactly
  // that fund. `undefined` when nothing is picked so `fundIds` is omitted.
  const effectiveFundIds = useMemo(() => (funds.length ? funds : undefined), [funds]);

  const {
    data,
    loading: viewLoading,
    error,
    stopPolling,
    startPolling,
    refetch: refetchView,
  } = useCoverageViewQuery({
    variables: {
      input: {
        arenaId: arenaId!,
        groupBy: "FUND_BY_PERIOD",
        dateRange: { start: dateRange.start, end: dateRange.end },
        seedFundIds,
        filter: {
          fundIds: effectiveFundIds,
          states: toCoverageFilterStates(statuses),
        },
      },
    },
    fetchPolicy: "cache-and-network",
    skip: seedFundIds.length === 0 || arenaId === undefined,
    pollInterval: COVERAGE_POLL_MS,
    // Shed the poll while the tab is backgrounded, without tearing down the
    // query — the next foreground tick resumes normally.
    skipPollAttempt: () => document.hidden,
  });

  const rows = useMemo(() => data?.coverageView.rows ?? [], [data]);
  const periods = useMemo(() => data?.coverageView.periods ?? [], [data]);

  // A dead fay session (expired/invalid JWT, or the arena kill-switch/permission
  // flipping off while the page is open) won't recover by retrying, so stop
  // polling on auth failure. The flag is also read by refreshCoverage so an
  // on-demand refresh can't resurrect polling after the terminal stop. Watch the
  // seed error too, not just the view error: in an arena with no packaged funds
  // the view query is `skip`ped and only the seed query runs, so a seed-only 403
  // would otherwise be missed. Report up to the page gate so the whole page
  // swaps to a generic error page instead of an inline error line.
  const seedAuthError = seedError && isFayAuthFailure(seedError) ? seedError : undefined;
  const reportAuthFailure = useReportFayAuthFailure();
  const authDeadRef = useRef(false);
  useEffect(() => {
    const viewAuthError = error && isFayAuthFailure(error) ? error : undefined;
    if (viewAuthError || seedAuthError) {
      authDeadRef.current = true;
      stopPolling();
      stopSeedPolling();
      // Report every error that matched, not just one: the gate latches its
      // reason on the first *recognized* refusal, so when the view error is an
      // unrecognizable shape (e.g. a dead session's 401) a recognized seed
      // refusal must still get through to name the reason.
      if (viewAuthError) {
        reportAuthFailure(viewAuthError);
      }
      if (seedAuthError) {
        reportAuthFailure(seedAuthError);
      }
    }
  }, [error, seedAuthError, stopPolling, stopSeedPolling, reportAuthFailure]);

  // Mirror the query controls into refs so the debounced refreshCoverage
  // (created once) always calls the current handlers instead of a stale closure.
  const ctlRef = useRef({
    refetchSeed,
    refetchCatalog,
    refetchView,
    stopSeedPolling,
    stopPolling,
    startSeedPolling,
    startPolling,
  });
  ctlRef.current = {
    refetchSeed,
    refetchCatalog,
    refetchView,
    stopSeedPolling,
    stopPolling,
    startSeedPolling,
    startPolling,
  };
  // Refresh is arena-gated only. The seed refetch is always safe once arena
  // resolves — it's what discovers newly-appearing funds (e.g. after the very
  // first approve in a previously-empty arena). Catalog + view queries are
  // `skip`ped until `seedFundIds` is non-empty, so we skip THEIR refetch in
  // that window to avoid an empty-variables round trip; once the seed
  // populates, they auto-fire via the hook re-render.
  const arenaReadyRef = useRef(false);
  arenaReadyRef.current = arenaId !== undefined;
  const hasFundsRef = useRef(false);
  hasFundsRef.current = seedFundIds.length > 0;

  const refreshCoverage = useMemo(() => {
    const run = async (): Promise<void> => {
      if (authDeadRef.current || !arenaReadyRef.current) {
        // No-op: an auth-dead session won't recover, and without a
        // resolved arena there's no query to fire. Do NOT restart polling
        // here — resurrecting the terminal auth-dead stop would hammer
        // fay with credentials that will keep failing.
        return;
      }
      const ctl = ctlRef.current;
      ctl.stopSeedPolling();
      ctl.stopPolling();
      try {
        // Always resync the seed — the "empty grid → first approve creates
        // the first package" scenario needs this to discover the new fund
        // (without it, the grid waits a full poll tick to surface it).
        const refetches: Promise<unknown>[] = [ctl.refetchSeed()];
        if (hasFundsRef.current) {
          refetches.push(ctl.refetchCatalog(), ctl.refetchView());
        }
        await Promise.all(refetches);
      } catch {
        // Best-effort refresh; the 15s poll tick below still resumes and
        // will retry, so the grid self-heals from a transient error.
      } finally {
        if (!authDeadRef.current) {
          ctl.startSeedPolling(COVERAGE_POLL_MS);
          ctl.startPolling(COVERAGE_POLL_MS);
        }
      }
    };
    // Callers fire and forget — `run` handles its own failures — so the exposed
    // refresh stays a plain `() => void`.
    return debounce(() => void run(), REFRESH_DEBOUNCE_MS, {
      leading: true,
      trailing: false,
    });
  }, []);

  // Cancel the trailing edge on unmount so a stray refresh doesn't fire against
  // a torn-down component.
  useEffect(() => () => refreshCoverage.cancel(), [refreshCoverage]);

  // Report loading through the seed phase too. The view query is `skip`ped
  // until we have a seed universe, and a skipped Apollo query reports
  // `loading: false` — so without this, the component briefly sees
  // `loading=false, rows=[]` between "seed resolved" and "view query started"
  // and flashes the empty grid. We're loading while the seed is in flight, or
  // while we have a seed universe but the view has neither data nor an error
  // yet (covers the skipped-but-pending gap and the in-flight first fetch).
  // `!error` keeps the error branch reachable; the seed-empty case (no funds)
  // correctly falls through to a non-loading empty grid.
  const loading = seedLoading || (seedFundIds.length > 0 && !data && !error) || viewLoading;

  return {
    fundOptions,
    rows,
    periods,
    loading,
    error: error ?? seedAuthError,
    refreshCoverage,
  };
}
